/**
 * Money formatters.
 *
 *   formatINR(value, opts?)   → "₹1,23,45,678.50"   (Indian numbering, ≥ ₹1L by default)
 *   formatINRCompact(value)   → "₹1.23 Cr"           (lakh / crore abbreviation)
 *   formatUSD(value, opts?)   → "$1,234,567.50"
 *   formatMoneyAuto(value, currency, opts?)
 *
 * Indian-numbering rules:
 *   • Default: Indian grouping (lakh/crore commas) when the *integer part*
 *     is ≥ 1 lakh AND the currency is INR. Below that threshold the formatter
 *     falls back to plain Western grouping so small values look natural.
 *   • Override per-call with `numberingMode: 'indian' | 'western' | 'auto'`.
 *   • User override at app level is in Settings → Number Format.
 *
 * Sign handling:
 *   • By default the sign is INSIDE the symbol: `-₹1,234.50`. For accounting
 *     mode (`signMode: 'paren'`) negatives render as `(₹1,234.50)`.
 *   • The returned object's `sign` field is the data-sign attribute callers
 *     attach to the wrapper element so the CSS in `globals.css` colours it.
 */

import type { INR, USD } from '@/types/money';
import type { NumberLocaleId } from '@/types/domain';

import { LAKH, CRORE, signOf, type Sign } from '../math/money';

export type NumberingMode = 'indian' | 'western' | 'auto';
export type SignMode = 'leading' | 'paren';

export interface FormatMoneyResult {
  /** The fully-formatted string ready for the DOM. */
  text: string;
  /** Sign of the *displayed* value, suitable for `data-sign`. */
  sign: Sign;
  /** Magnitude bucket used by compact formatters ('L' / 'Cr' / etc.); 'unit' for plain. */
  scale: 'unit' | 'thousand' | 'lakh' | 'crore' | 'million' | 'billion';
}

export interface FormatINROptions {
  numberingMode?: NumberingMode;
  /** Decimal places shown. Defaults to 2 for amounts ≥ ₹1L, 0 otherwise. */
  digits?: number;
  /** Whether to include the ₹ symbol. */
  withSymbol?: boolean;
  /** Sign placement style. */
  signMode?: SignMode;
  /** Threshold at which we switch into Indian grouping. Default `LAKH` (₹1,00,000). */
  indianGroupingThreshold?: number;
}

export interface FormatUSDOptions {
  digits?: number;
  withSymbol?: boolean;
  signMode?: SignMode;
}

export interface FormatINRCompactOptions {
  /** Decimal places in the compact value (default 2). */
  digits?: number;
  withSymbol?: boolean;
  signMode?: SignMode;
  /** Force a scale ('lakh' or 'crore'); auto picks the largest fitting unit. */
  forceScale?: 'lakh' | 'crore';
}

/* ============================================================================
 * Constants and helpers
 * ============================================================================ */

const INR_SYMBOL = '₹';
const USD_SYMBOL = '$';
const NBSP = ' ';

const intl = (locale: NumberLocaleId, opts: Intl.NumberFormatOptions): Intl.NumberFormat =>
  new Intl.NumberFormat(locale, opts);

const formatAbs = (
  abs: number,
  locale: NumberLocaleId,
  digits: number,
): string =>
  intl(locale, {
    useGrouping: true,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(abs);

const wrapSign = (
  body: string,
  symbol: string | null,
  sign: Sign,
  mode: SignMode,
): string => {
  const prefix = symbol === null ? '' : symbol;
  if (sign === 'short') {
    return mode === 'paren' ? `(${prefix}${body})` : `-${prefix}${body}`;
  }
  return `${prefix}${body}`;
};

/* ============================================================================
 * INR — the workhorse
 * ============================================================================ */

export const formatINR = (value: INR, opts: FormatINROptions = {}): FormatMoneyResult => {
  const raw = value as unknown as number;
  if (!Number.isFinite(raw)) {
    return { text: '—', sign: 'zero', scale: 'unit' };
  }

  const {
    numberingMode = 'auto',
    withSymbol = true,
    signMode = 'leading',
    indianGroupingThreshold = LAKH,
  } = opts;

  const sign = signOf(raw);
  const abs = Math.abs(raw);
  const digits = opts.digits ?? (abs >= indianGroupingThreshold ? 2 : 2);

  let locale: NumberLocaleId;
  if (numberingMode === 'indian') {
    locale = 'en-IN';
  } else if (numberingMode === 'western') {
    locale = 'en-US';
  } else {
    locale = abs >= indianGroupingThreshold ? 'en-IN' : 'en-US';
  }

  const body = formatAbs(abs, locale, digits);
  const symbol = withSymbol ? INR_SYMBOL : null;
  const text = wrapSign(body, symbol, sign, signMode);
  return { text, sign, scale: 'unit' };
};

/* ============================================================================
 * INR — compact (lakh / crore abbreviation)
 *
 *   abs >= ₹1 Cr  → "₹X.XX Cr"
 *   abs >= ₹1 L   → "₹X.XX L"
 *   else          → plain formatINR with 0 digits
 *
 * The space between the number and the unit is a NBSP so the unit doesn't
 * wrap onto the next line in a tile.
 * ============================================================================ */

export const formatINRCompact = (
  value: INR,
  opts: FormatINRCompactOptions = {},
): FormatMoneyResult => {
  const raw = value as unknown as number;
  if (!Number.isFinite(raw)) {
    return { text: '—', sign: 'zero', scale: 'unit' };
  }

  const { digits = 2, withSymbol = true, signMode = 'leading', forceScale } = opts;
  const sign = signOf(raw);
  const abs = Math.abs(raw);

  let unit: 'Cr' | 'L' | null = null;
  let divisor = 1;
  let scale: FormatMoneyResult['scale'] = 'unit';

  if (forceScale === 'crore' || (!forceScale && abs >= CRORE)) {
    unit = 'Cr';
    divisor = CRORE;
    scale = 'crore';
  } else if (forceScale === 'lakh' || (!forceScale && abs >= LAKH)) {
    unit = 'L';
    divisor = LAKH;
    scale = 'lakh';
  }

  const symbol = withSymbol ? INR_SYMBOL : null;
  if (unit === null) {
    // Below ₹1L — defer to plain en-US grouping with zero decimals.
    const body = formatAbs(abs, 'en-US', 0);
    return { text: wrapSign(body, symbol, sign, signMode), sign, scale: 'unit' };
  }

  const scaled = abs / divisor;
  // Compact mode uses Western grouping for the scaled number — never "1,2.34 Cr".
  const body = formatAbs(scaled, 'en-US', digits);
  return {
    text: wrapSign(`${body}${NBSP}${unit}`, symbol, sign, signMode),
    sign,
    scale,
  };
};

/* ============================================================================
 * USD — Western grouping always
 * ============================================================================ */

export const formatUSD = (value: USD, opts: FormatUSDOptions = {}): FormatMoneyResult => {
  const raw = value as unknown as number;
  if (!Number.isFinite(raw)) {
    return { text: '—', sign: 'zero', scale: 'unit' };
  }
  const { digits = 2, withSymbol = true, signMode = 'leading' } = opts;
  const sign = signOf(raw);
  const abs = Math.abs(raw);
  const body = formatAbs(abs, 'en-US', digits);
  const symbol = withSymbol ? USD_SYMBOL : null;
  return { text: wrapSign(body, symbol, sign, signMode), sign, scale: 'unit' };
};

/* ============================================================================
 * Polymorphic entry — picks INR vs USD based on currency.
 * ============================================================================ */

export type FormatMoneyOptions =
  | ({ currency: 'INR' } & FormatINROptions & { compact?: boolean })
  | ({ currency: 'USD' } & FormatUSDOptions);

/** Polymorphic entry — `value` is either an `INR` or a `USD` brand; the
 *  `currency` discriminator on `opts` selects the underlying formatter. */
export const formatMoneyAuto = (
  value: INR | USD,
  opts: FormatMoneyOptions,
): FormatMoneyResult => {
  if (opts.currency === 'INR') {
    if (opts.compact) {
      const { compact: _compact, currency: _c, ...rest } = opts;
      void _compact;
      void _c;
      return formatINRCompact(value as INR, rest);
    }
    const { currency: _c, ...rest } = opts;
    void _c;
    return formatINR(value as INR, rest);
  }
  const { currency: _c, ...rest } = opts;
  void _c;
  return formatUSD(value as USD, rest);
};

/* ============================================================================
 * Quick string-only helpers — handy for log lines, toasts, and tests where
 * callers don't need the result envelope.
 * ============================================================================ */

export const formatINRString = (value: INR, opts: FormatINROptions = {}): string =>
  formatINR(value, opts).text;

export const formatINRCompactString = (value: INR, opts: FormatINRCompactOptions = {}): string =>
  formatINRCompact(value, opts).text;

export const formatUSDString = (value: USD, opts: FormatUSDOptions = {}): string =>
  formatUSD(value, opts).text;
